// Three-button export toolbar (PNG / SVG / CSV).
//
// PNG + SVG depend on the live <svg> being in the DOM with a known id,
// so we disable them for the table chart type — the table has no SVG to
// serialise. CSV stays available regardless because it operates on the
// underlying rows.
import type { ChartType, AggregationResult } from "../../types/chart";
import { useState } from "react";
import { exportCsv, exportPng, exportSvg } from "./chartExport";

interface Props {
  result: AggregationResult;
  exportId: string;
  title: string;
  chartType: ChartType;
  disabled?: boolean;
}

export function ChartExportToolbar({ result, exportId, title, chartType, disabled = false }: Props) {
  const [error, setError] = useState<string | null>(null);
  const ctx = {
    exportId,
    title,
    groupBy: result.chart_spec.group_by,
    chartType,
    rows: result.rows,
  };
  const isTable = chartType === "table";
  const runExport = (operation: () => void | Promise<void>) => {
    setError(null);
    void Promise.resolve().then(operation).catch((reason: unknown) => {
      setError(reason instanceof Error ? reason.message : "Chart export failed.");
    });
  };
  return (
    <div>
      <div className="chart-toolbar" role="toolbar" aria-label="Export chart">
      <button
        type="button"
        className="chart-toolbar__btn"
        onClick={() => runExport(() => exportPng(ctx))}
        disabled={isTable || disabled}
        title={disabled ? "Export disabled while data refreshes" : isTable ? "PNG export disabled for table view" : "Download PNG"}
      >
        PNG
      </button>
      <button
        type="button"
        className="chart-toolbar__btn"
        onClick={() => runExport(() => exportSvg(ctx))}
        disabled={isTable || disabled}
        title={disabled ? "Export disabled while data refreshes" : isTable ? "SVG export disabled for table view" : "Download SVG"}
      >
        SVG
      </button>
      <button
        type="button"
        className="chart-toolbar__btn"
        onClick={() => runExport(() => exportCsv(ctx))}
        disabled={disabled}
        title={disabled ? "Export disabled while data refreshes" : "Download CSV"}
      >
        CSV
      </button>
      </div>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
